import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  ViveApiConfigInterface,
  ViveApiRequestClientOptions
} from './vive-api.config.interface';

export const LOAD_BALANCER_TIMEOUT_MS = 60_000;

/**
 * The config for vive-app-v2, which still makes most of its API calls through
 * Angular's HttpClient and depends on its HTTP interceptors: the loading mask,
 * error toasts, the LoginModal on a lapsed session (with its route
 * exclusions), and the terms-and-conditions gate. Sending these requests
 * through HttpClient as well puts them behind the same interceptors.
 * `vive-api.config.front-end` sends them with `fetch`, which no interceptor
 * sees.
 *
 * Session mode only: the HttpOnly session cookie authenticates, and the
 * interceptor sets `withCredentials` on API requests. There is no
 * JavaScript-held token.
 */

// A Response built with a body for one of these throws a TypeError.
const NULL_BODY_STATUSES = new Set([204, 205, 304]);

class ViveApiConfigSingleton implements ViveApiConfigInterface {
  public readonly sessionMode = true;
  // The interceptor opens the LoginModal and retries, and leaves alone the
  // credential routes where a 401 is a refused credential. A 401 that gets
  // past it is the caller's answer, and its body (an "Invalid credentials"
  // message, say) must reach the caller rather than a second sign-in prompt.
  public readonly transportHandlesReauth = true;
  private _http: HttpClient | null = null;

  /**
   * Hand over the app's HttpClient. Must be called via provideAppInitializer
   * before any API request fires: this file is evaluated on import, outside
   * Angular's injector.
   */
  public configure(http: HttpClient): void {
    this._http = http;
  }

  public getViveApiBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  public logDebugMessage(message: string): void {
    console.log(message);
  }

  public logErrorMessage(message: string): void {
    console.error(message);
  }

  public logMetrics(_params: {
    method: string;
    endpoint: string;
    responseStatus: number;
    duration: number;
  }): void {
    // Not reported, as in vive-api.config.front-end.
  }

  public async getUserToken(): Promise<string | null> {
    return null;
  }

  /** Never called: `transportHandlesReauth` keeps the base service from asking. */
  public async handleTokenExpired(): Promise<string | null> {
    return null;
  }

  public async makeApiClientRequest({
    url,
    method,
    requestTimeout,
    additionalHeaders,
    data,
    context
  }: ViveApiRequestClientOptions): Promise<Response> {
    const http = this.requireHttpClient();
    const isFormData = data instanceof FormData;

    try {
      const response = await lastValueFrom(
        http.request(method, relativeApiPath(url), {
          // Serialized here, as `vive-api.config.front-end` does, so a string
          // or number body reaches the API exactly as it would through fetch.
          body: data == null ? null : isFormData ? data : JSON.stringify(data),
          headers: new HttpHeaders({
            ...additionalHeaders,
            ...(!isFormData &&
              data != null && {
                'Content-Type': 'application/json; charset=UTF-8'
              })
          }),
          context: toHttpContext(context),
          observe: 'response',
          // Not 'json': the interceptor revives ISO date strings into Dates
          // in JSON bodies, and the generated models declare them as strings.
          // It still decodes an ArrayBuffer error body for its toast.
          responseType: 'arraybuffer',
          timeout: requestTimeout ?? LOAD_BALANCER_TIMEOUT_MS
        })
      );

      return toFetchResponse(response.status, response.headers, response.body);
    } catch (error) {
      // Anything else was raised by an interceptor rather than the network,
      // a declined terms-and-conditions gate for one, and stays as it is.
      if (!(error instanceof HttpErrorResponse)) {
        throw error;
      }

      // The API answered: the base service reads the status and body and
      // throws its own ViveApiError, exactly as it would after fetch.
      if (error.status > 0) {
        return toFetchResponse(error.status, error.headers, error.error);
      }

      // HttpClient reports its own timeout as this DOMException, which the
      // base service turns into a 504 ViveApiError.
      if (
        error.error instanceof DOMException &&
        error.error.name === 'TimeoutError'
      ) {
        throw error.error;
      }

      // No answer at all. fetch rejects with a TypeError here, and that is
      // what the base service checks for.
      throw new TypeError('Failed to fetch', { cause: error });
    }
  }

  private requireHttpClient(): HttpClient {
    if (!this._http) {
      throw new Error(
        'vive-api.config not initialized — call configure() before making API requests'
      );
    }
    return this._http;
  }
}

/**
 * The URL as a path on the API host. HttpInterceptorInterceptor only treats a
 * relative `/api` or `/auth` URL as an API request: it puts `viveApiUrl` in
 * front, sets `withCredentials` and owns the 401. Both that and
 * `environment.apiBaseUrl` come from `config/config.ts`, so dropping the
 * origin cannot move the request to another host. The check below says so if
 * that ever stops being true.
 */
function relativeApiPath(url: URL): string {
  const apiOrigin = new URL(environment.apiBaseUrl).origin;
  if (url.origin !== apiOrigin) {
    throw new Error(
      `vive-api request to ${url.origin} is not on the API host ${apiOrigin}`
    );
  }
  return `${url.pathname}${url.search}`;
}

/**
 * Each `context` key set to `true` becomes an HttpContext flag of that name.
 * HttpInterceptorInterceptor reads a flag by its token's default value, so
 * `{ SkipLoadingMask: true }` and `{ ManuallyHandleError: true }` mean what
 * they mean on an HttpClient call.
 */
function toHttpContext(
  context: Readonly<Record<string, unknown>> | undefined
): HttpContext {
  const httpContext = new HttpContext();
  for (const [name, value] of Object.entries(context ?? {})) {
    if (value === true) {
      httpContext.set(new HttpContextToken(() => name), null);
    }
  }
  return httpContext;
}

/** The fetch Response the base service reads, built from HttpClient's answer. */
function toFetchResponse(
  status: number,
  headers: HttpHeaders,
  body: unknown
): Response {
  const responseHeaders = new Headers();
  for (const name of headers.keys()) {
    for (const value of headers.getAll(name) ?? []) {
      responseHeaders.append(name, value);
    }
  }

  return new Response(
    NULL_BODY_STATUSES.has(status) ? null : toResponseBody(body),
    { status, headers: responseHeaders }
  );
}

function toResponseBody(body: unknown): BodyInit | null {
  if (body == null) {
    return null;
  }
  if (
    body instanceof ArrayBuffer ||
    body instanceof Blob ||
    typeof body === 'string'
  ) {
    return body;
  }
  // The interceptor replaces a JSON error body with its parsed value before
  // the error reaches here.
  return JSON.stringify(body);
}

const viveApiConfig = new ViveApiConfigSingleton();

export default viveApiConfig;
